import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Spinner, Alert } from "reactstrap";
import { Page, PageHeader, Card, COLOR } from "../components/ui";
import { getAuthHeaders } from "../utils/auth";
import { API_URL_historical_kpis, API_URL_historical_kpis_meta } from "../constants";

const fmt = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  // ROI-like values show as fixed %; big money shows as thousands-separated.
  if (Math.abs(n) < 10) return n.toFixed(3);
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
};

export default function BoxScoreHistorical() {
  const [meta, setMeta] = useState({ years: [], value_streams: [] });
  const [year, setYear] = useState(null);
  const [stream, setStream] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(API_URL_historical_kpis_meta, { headers: getAuthHeaders() })
      .then((res) => {
        setMeta(res.data || { years: [], value_streams: [] });
        if (res.data?.years?.length) {
          // Prefer the most recent complete year (not the current), else last available.
          setYear(res.data.years[res.data.years.length - 1]);
        }
        if (res.data?.value_streams?.length) {
          setStream(res.data.value_streams[0]);
        }
      })
      .catch(() => setErr("No se pudieron cargar los metadatos históricos."))
      .finally(() => setLoadingMeta(false));
  }, []);

  const fetchData = useCallback(async () => {
    if (!year || !stream) return;
    setLoadingData(true);
    setErr("");
    try {
      const res = await axios.get(API_URL_historical_kpis, {
        headers: getAuthHeaders(),
        params: { year, value_stream: stream, shape: "matrix" },
      });
      setMatrix(res.data?.[stream] || {});
    } catch (e) {
      setErr("No se pudieron cargar los KPIs históricos.");
    } finally {
      setLoadingData(false);
    }
  }, [year, stream]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const kpiRows = useMemo(() => Object.keys(matrix).sort(), [matrix]);
  const weekCount = year === 2024 ? 52 : 53;
  const weeks = useMemo(
    () => Array.from({ length: weekCount }, (_, i) => i + 1),
    [weekCount]
  );

  const rowTotal = (kpiName) => {
    const row = matrix[kpiName] || {};
    return weeks.reduce((s, w) => {
      const v = row[`W${w}`];
      return typeof v === "number" ? s + v : s;
    }, 0);
  };

  const th = {
    padding: "8px 10px",
    textAlign: "right",
    fontSize: "0.7rem",
    fontWeight: 600,
    color: COLOR.muted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    borderBottom: `1px solid ${COLOR.rule}`,
    background: COLOR.subtle,
    position: "sticky",
    top: 0,
    whiteSpace: "nowrap",
  };
  const td = { padding: "6px 10px", textAlign: "right", fontSize: "0.8125rem", borderBottom: `1px solid ${COLOR.rule}`, whiteSpace: "nowrap" };
  const stickyLeft = { position: "sticky", left: 0, background: COLOR.surface, textAlign: "left", fontWeight: 500 };

  return (
    <Page>
      <PageHeader
        title="Box Score histórico"
        subtitle="Datos semanales importados del histórico 2024–2026."
      />

      {err && <Alert color="danger">{err}</Alert>}

      <Card
        title="Selección"
        headerRight={
          <span style={{ color: COLOR.muted, fontSize: "0.8rem" }}>
            {loadingMeta ? "Cargando…" : `${meta.years.length} años · ${meta.value_streams.length} value streams`}
          </span>
        }
      >
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ minWidth: 140 }}>
            <label style={{ fontSize: "0.75rem", color: COLOR.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>
              Año
            </label>
            <select
              value={year || ""}
              onChange={(e) => setYear(Number(e.target.value))}
              disabled={loadingMeta}
              style={{ padding: "8px 10px", border: `1px solid ${COLOR.rule}`, borderRadius: 8, background: "#fff", minWidth: 120 }}
            >
              {meta.years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 240, flex: 1 }}>
            <label style={{ fontSize: "0.75rem", color: COLOR.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>
              Value Stream
            </label>
            <select
              value={stream || ""}
              onChange={(e) => setStream(e.target.value)}
              disabled={loadingMeta}
              style={{ padding: "8px 10px", border: `1px solid ${COLOR.rule}`, borderRadius: 8, background: "#fff", width: "100%" }}
            >
              {meta.value_streams.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </Card>

      <Card
        title={
          <>
            {stream || "—"}
            <span style={{ color: COLOR.muted, fontWeight: 500, marginLeft: 8 }}>· {year || "—"}</span>
          </>
        }
        headerRight={
          <span style={{ color: COLOR.muted, fontSize: "0.8rem" }}>
            {loadingData ? "Cargando…" : `${kpiRows.length} KPIs · ${weekCount} semanas`}
          </span>
        }
        padded={false}
        scrollX
      >
        {loadingData ? (
          <div style={{ textAlign: "center", padding: "48px 20px" }}>
            <Spinner color="primary" />
          </div>
        ) : kpiRows.length === 0 ? (
          <div style={{ textAlign: "center", padding: "36px 20px", color: COLOR.muted }}>
            Sin datos para este año / value stream.
          </div>
        ) : (
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...th, ...stickyLeft, textAlign: "left" }}>KPI</th>
                <th style={{ ...th, background: COLOR.subtle, color: COLOR.ink }}>Total</th>
                {weeks.map((w) => (
                  <th key={w} style={th}>W{w}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {kpiRows.map((kpi) => (
                <tr key={kpi}>
                  <td style={{ ...td, ...stickyLeft }}>{kpi}</td>
                  <td style={{ ...td, fontWeight: 600, background: COLOR.subtle }}>{fmt(rowTotal(kpi))}</td>
                  {weeks.map((w) => {
                    const v = matrix[kpi]?.[`W${w}`];
                    return (
                      <td key={w} style={{ ...td, color: v == null ? COLOR.muted : COLOR.ink }}>
                        {fmt(v)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </Page>
  );
}
